import { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useQuery } from '@apollo/client';
import { displayName, abbvToApparatusName } from '../../utilities/conversions';
import { MEDIAURL } from '../../utilities/constants';
import GetMatchById from '../../apollo/queries/GetMatchById.graphql';
import * as Icons from '../helpers/icons';
import MatchUp from './matchup';
import './matchcountdown.css';

function CountDownTimer({ date }) {
  const getCountdown = () => {
    const timeRemaining = new Date(date) - new Date();
    const countdown = {
      Days: Math.floor(Math.abs(timeRemaining) / (1000 * 60 * 60 * 24)),
      Hours: Math.floor((Math.abs(timeRemaining) / (1000 * 60 * 60)) % 24),
      Minutes: Math.floor((Math.abs(timeRemaining) / 1000 / 60) % 60),
      Seconds: Math.floor((Math.abs(timeRemaining) / 1000) % 60),
    };
    return countdown;
  };
  const [countdown, setCountdown] = useState(getCountdown());

  useEffect(() => {
    const timer = setTimeout(() => {
      setCountdown(getCountdown());
    }, 1000);
    return () => clearTimeout(timer);
  });

  return (
    <div className="countdownTimer">
      <ul>
        <li>{countdown.Days}d•</li>
        <li>
          {countdown.Hours.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}
          h•
        </li>
        <li>
          {countdown.Minutes.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}
          m•
        </li>
        <li>
          {countdown.Seconds.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}
          s
        </li>
      </ul>
    </div>
  );
}

function MatchCountdown({ sessionId, apparatus, isScoreboard = false }) {
  const [thumbnailExpanded, setThumbnailExpanded] = useState(false);
  const { data } = useQuery(GetMatchById, {
    variables: {
      id: sessionId,
    },
  });

  if (!data) {
    return null;
  }

  const { getSession } = data;
  const {
    startAt,
    sessionTeams,
    name,
    gender,
    featureFlags: rawFeatureFlags,
  } = getSession;
  let parsedFeatureFlags = {};
  try {
    parsedFeatureFlags = rawFeatureFlags ? JSON.parse(rawFeatureFlags) : {};
  } catch (e) {
    parsedFeatureFlags = {};
  }
  const customBackgroundUrl =
    typeof parsedFeatureFlags?.customBackground === 'string'
      ? parsedFeatureFlags.customBackground.startsWith('http')
        ? parsedFeatureFlags.customBackground
        : `${MEDIAURL}${parsedFeatureFlags.customBackground}`
      : null;
  const customBgTextColor = parsedFeatureFlags?.customBgTextColor || null;
  const customBgApparatus = parsedFeatureFlags?.customBgApparatus || null;
  const sortedSessionTeams = sessionTeams.items
    .filter((st) => !st._deleted)
    .sort((a, b) => {
      return a.order - b.order;
    });

  const teams = sortedSessionTeams;
  const teamA = teams.length > 0 && teams[0].team;
  const teamB = teams.length > 1 && teams[1].team;
  const teamC = teams.length > 2 && teams[2].team;
  const teamD = teams.length > 3 && teams[3].team;
  const teamE = teams.length > 4 && teams[4].team;
  const teamF = teams.length > 5 && teams[5].team;
  const teamG = teams.length > 6 && teams[6].team;
  const teamH = teams.length > 7 && teams[7].team;

  //const customTheme = name.includes('Super 16') ? 'meanCountdown' : null;
  const isArkansas =
    name.includes('Arkansas') &&
    name.split(' ')?.[0] === 'Arkansas' &&
    teamA.id === 'lYQFBogMKiSAA21nVSNAF' &&
    gender === 'FEMALE'
      ? 'arkansas'
      : null;
  const isMichigan =
    name.includes('Michigan') &&
    name.split(' ')?.[0] === 'Michigan' &&
    teamA.id === 'YR7-a5UlVbf3aKaTX16o2' &&
    gender === 'FEMALE'
      ? 'michiganW'
      : null;
  const isOklahoma =
    name.includes('Oklahoma') &&
    name.trim().split(' ')?.[0] === 'Oklahoma' &&
    teamA.id === 'NBrvC6YJkMpnKv1I8loqQ' &&
    gender === 'FEMALE'
      ? 'oklahomaW'
      : null;
  const isOklahomaM =
    name.includes('Oklahoma') &&
    name.trim().split(' ')?.[0] === 'Oklahoma' &&
    gender === 'MALE'
      ? 'oklahomaM'
      : null;
  const isNebraska =
    name.includes('Nebraska') &&
    name.trim().split(' ')?.[0] === 'Nebraska' &&
    teamA.id === 'fZWYeAddqxfITfZSLo65a' &&
    gender === 'FEMALE'
      ? 'nebraskaW'
      : null;
  const isNebraskaM =
    name.includes('Nebraska') &&
    name.trim().split(' ')?.[0] === 'Nebraska' &&
    gender === 'MALE'
      ? 'nebraskaM'
      : null;
  const isUtah =
    name.includes('Utah') &&
    name.split(' ')?.[0] === 'Utah' &&
    teamA.id === 'j-8H25znXE8IcMuotCPb7' &&
    gender === 'FEMALE'
      ? 'utah'
      : null;
  const isBigTen =
    name.includes('Big Ten Championships') && gender === 'FEMALE'
      ? 'bigten'
      : null;
  const isMIC =
    name.includes('MIC Conference Championship') && gender === 'FEMALE'
      ? 'MIC'
      : null;
  const isBig12 =
    name.includes('Big 12 Championship') && gender === 'FEMALE'
      ? 'big12'
      : null;
  const isUSAGCC =
    name.includes('USAG Collegiate Championships') && gender === 'MALE'
      ? 'USAGCC'
      : null;
  const isGEC =
    name.includes('GEC Championship') && gender === 'FEMALE' ? 'GEC' : null;
  const isMAC =
    name.includes('MAC Championships') ||
    (name.includes('MAC Conference Championship') && gender === 'FEMALE')
      ? 'MAC'
      : null;
  const isEAGL =
    name.includes('EAGL Conference Championship') && gender === 'FEMALE'
      ? 'EAGL'
      : null;
  const isSEC =
    name.includes('SEC Championship') && gender === 'FEMALE' ? 'SEC' : null;
  const isMW =
    name.includes('Mountain West Championship') && gender === 'FEMALE'
      ? 'MW'
      : null;
  const isMPSFW =
    name.includes('MPSF Championship') && gender === 'FEMALE' ? 'MPSFW' : null;
  const isMPSFM =
    name.includes('MPSF Championship') && gender === 'MALE' ? 'MPSFM' : null;
  const isNCGA =
    name.includes('NCGA Championships') && gender === 'FEMALE' ? 'NCGA' : null;
  const isACC =
    name.includes('ACC Gymnastics Championship') ||
    (name.includes('ACC Championship') && gender === 'FEMALE')
      ? 'ACC'
      : null;
  const isArkRegional =
    name.includes("NCAA Regional '24 @ Arkansas") && gender === 'FEMALE'
      ? 'ArkRegional'
      : null;
  const isCalRegional =
    name.includes("NCAA Regional '24 @ Cal") && gender === 'FEMALE'
      ? 'CalRegional'
      : null;

  const isAlaRegional =
    name.includes("NCAA Regional '25 @ Alabama") && gender === 'FEMALE'
      ? 'AlaRegional'
      : null;
  const isUtahRegional =
    name.includes("NCAA Regional '25 @ Utah") && gender === 'FEMALE'
      ? 'UtahRegional'
      : null;
  const isWashRegional =
    name.includes("NCAA Regional '25 @ Washington") && gender === 'FEMALE'
      ? 'WashRegional'
      : null;
  const isNCAAChampsW =
    name.includes("NCAA '25") && gender === 'FEMALE' ? 'NCAAChampsW' : null;

  const isECAC =
    name.includes('ECAC Championship') && gender === 'MALE' ? 'ECAC' : null;

  const isUSAGCN =
    name.includes('USAGCN') && gender === 'FEMALE' ? 'USAGCN' : null;

  const isSprouts =
    name.includes('Sprouts') && gender === 'FEMALE' ? 'sprouts' : null;

  const isWasatch =
    name.includes('Wasatch Classic') && gender === 'FEMALE' ? 'wasatch' : null;

  const isBestOfUtah =
    name.includes('Best of Utah') && gender === 'FEMALE' ? 'bestofutah' : null;

  const isWCGNIC =
    name.includes('WCGNIC') && gender === 'FEMALE' ? 'WCGNIC' : null;

  const themeBackgrounds = {
    arkansas: '/media/arkansas/hog_background.jpg',
    michiganW: '/media/michigan/w_background.jpg',
    oklahomaW: '/media/oklahoma/w_background.jpg',
    oklahomaM: '/media/oklahoma/m_background.jpg',
    nebraskaW: '/media/nebraska/w_background.jpg',
    nebraskaM: '/media/nebraska/m_background.jpg',
    bigten: '/media/bigten/btn_background_only.jpg',
    USAGCC: '/media/usagcc/usagcc_background.jpg',
    WCGNIC: '/media/wcgnic/wcgnic_background.jpg',
    GEC: '/media/gec/gec_background.jpg',
    MAC: '/media/mac/mac_background.jpg',
    ACC: '/media/acc/acc_background.jpg',
    MIC: '/media/mic/mic_background.jpg',
    big12: '/media/big12/big12_background.jpg',
    SEC: '/media/sec/sec_background.jpg',
    EAGL: '/media/eagl/eagl_background.jpg',
    MW: '/media/mw/mw_background.jpg',
    MPSFW: '/media/mpsf/mpsf_background.jpg',
    MPSFM: '/media/mpsf/mpsf_background.jpg',
    NCGA: '/media/ncga/ncga_background.jpg',
    ECAC: '/media/ecac/ecac_background.jpg',
    USAGCN: '/media/usagcn/usagcn_background.jpg',
    sprouts: '/media/sprouts/sprouts_background.jpg',
    wasatch: '/media/wasatch/wasatch_background.jpg',
    bestofutah: '/media/bestofutah/bestofutah_background.jpg',
    utah: '/media/utah/utah_background.jpg',
  };

  const customTheme =
    isArkansas ||
    isMichigan ||
    isOklahoma ||
    isOklahomaM ||
    isNebraska ||
    isNebraskaM ||
    isBigTen ||
    isGEC ||
    isUSAGCC ||
    isMAC ||
    isACC ||
    isArkRegional ||
    isCalRegional ||
    isECAC ||
    isUSAGCN ||
    isSprouts ||
    isWasatch ||
    isBestOfUtah ||
    isUtah ||
    isMIC ||
    isBig12 ||
    isEAGL ||
    isSEC ||
    isMW ||
    isMPSFW ||
    isMPSFM ||
    isNCGA ||
    isUtahRegional ||
    isAlaRegional ||
    isWashRegional ||
    isNCAAChampsW ||
    isWCGNIC ||
    null;

  const match = {
    sessionId,
    teamA: displayName(teamA.name, teamA.altNames),
    teamALogo: JSON.parse(sortedSessionTeams[0].team.logos)?.metaData?.filename,
    teamB: teamB && displayName(teamB.name, teamB.altNames),
    teamBLogo:
      teamB && JSON.parse(sortedSessionTeams[1].team.logos)?.metaData?.filename,
    teamC: teamC && displayName(teamC.name, teamC.altNames),
    teamCLogo:
      teamC && JSON.parse(sortedSessionTeams[2].team.logos)?.metaData?.filename,
    teamD: teamD && displayName(teamD.name, teamD.altNames),
    teamDLogo:
      teamD && JSON.parse(sortedSessionTeams[3].team.logos)?.metaData?.filename,
    teamE: teamE && displayName(teamE.name, teamE.altNames),
    teamELogo:
      teamE && JSON.parse(sortedSessionTeams[4].team.logos)?.metaData?.filename,
    teamF: teamF && displayName(teamF.name, teamF.altNames),
    teamFLogo:
      teamF && JSON.parse(sortedSessionTeams[5].team.logos)?.metaData?.filename,
    teamG: teamG && displayName(teamG.name, teamG.altNames),
    teamGLogo:
      teamG && JSON.parse(sortedSessionTeams[6].team.logos)?.metaData?.filename,
    teamH: teamH && displayName(teamH.name, teamH.altNames),
    teamHLogo:
      teamH && JSON.parse(sortedSessionTeams[7].team.logos)?.metaData?.filename,
    gameTime: startAt,
  };

  const apparatusLabel = apparatus
    ? abbvToApparatusName[apparatus.toUpperCase()].toUpperCase()
    : null;

  return (
    <div
      className={[
        'matchCountdown',
        customBackgroundUrl && isScoreboard ? null : customTheme,
      ].join(' ')}
      style={
        customBackgroundUrl && isScoreboard
          ? {
              backgroundImage: `url('${customBackgroundUrl}')`,
              backgroundSize: 'cover',
            }
          : undefined
      }
    >
      <div
        className="zoom vCenter"
        style={{
          ...(customBgTextColor ? { color: customBgTextColor } : {}),
          ...(customBackgroundUrl && isScoreboard ? { display: 'none' } : {}),
        }}
      >
        <Row className="vCenter">
          <span className="matchName">{name}</span>
        </Row>
        <Row className="vCenter">
          <MatchUp match={match} disabled={true} />
        </Row>
        <Row className="vCenter">
          <CountDownTimer date={startAt} />
        </Row>
      </div>
      {(customBackgroundUrl ||
        (customTheme && themeBackgrounds[customTheme])) && (
        <div
          className={`countdownThumbnail${
            thumbnailExpanded ? ' expanded' : ''
          }`}
          style={{
            backgroundImage: `url('${
              customBackgroundUrl || themeBackgrounds[customTheme]
            }')`,
          }}
          onClick={() => setThumbnailExpanded(!thumbnailExpanded)}
        />
      )}
      <div
        className="byVirtius"
        style={customBgTextColor ? { color: customBgTextColor } : undefined}
      >
        {apparatus && customBgApparatus !== 'hidden' ? (
          <Row>
            <Col>{Icons?.[`${apparatus.toUpperCase()}noText`]}</Col>
            <Col className="vCenter" style={{ fontSize: '4rem' }}>
              {customBgApparatus === 'abbrev'
                ? apparatus.toUpperCase()
                : apparatusLabel}
            </Col>
          </Row>
        ) : (
          'by Virtius'
        )}
      </div>
    </div>
  );
}

export default MatchCountdown;
